import { CompanyOwnerCompany, CompanyOwnerUser, CompanyPayoutDetails, CompanyPayoutStatus } from '../types/companyOwner';
import { getCustomerAuthUser } from '../services/storage/customerFlow';
import {
  getCompanyOwnerAuthUser,
  getCompanyOwnerCompany,
  getCompanyOwnerPayoutDetails,
} from '../services/storage/companyOwnerFlow';
import { getAdminAuthUser } from '../services/storage/adminFlow';
import { getDriverAuthUser } from '../services/storage/driverFlow';
import { decodeJwtPayload } from './customerOnboarding';
import { nextStageForCustomer } from './customerOnboarding';
import { nextStageForAdmin } from './adminOnboarding';
import { nextStageForDriver } from './driverOnboarding';
import { isAdminRole } from '../services/api/admin';

export function isCompanyOwnerRole(roles: string[] | null | undefined): boolean {
  return (roles ?? []).some((role) => String(role).toUpperCase() === 'ROLE_COMPANY_OWNER');
}

export function isCompanyOwnerUser(user: CompanyOwnerUser | null | undefined): boolean {
  return Boolean(user && isCompanyOwnerRole(user.roles));
}

export function requiresCompanyPayoutSetup(
  payoutStatus: CompanyPayoutStatus | null | undefined,
): boolean {
  return payoutStatus === 'MISSING' || payoutStatus === 'REQUIRES_UPDATE' || payoutStatus === 'REJECTED';
}

export function getCompanyOwnerNextRoute(
  company: CompanyOwnerCompany | null | undefined,
  payoutDetails?: CompanyPayoutDetails | null,
): string {
  if (!company) return '/company/onboarding';

  const resolvedPayoutDetails = payoutDetails === undefined
    ? getCompanyOwnerPayoutDetails()
    : payoutDetails;

  return requiresCompanyPayoutSetup(resolvedPayoutDetails?.payoutStatus)
    ? '/company/payout-setup'
    : '/company/dashboard';
}

export function getNonCompanyAccessFallbackRoute(): string {
  return getCurrentSessionHomeRoute() ? '/access-denied?area=company' : '/login?role=company';
}

export function getCurrentSessionHomeRoute(): string | null {
  const adminUser = getAdminAuthUser();
  if (adminUser && isAdminRole(adminUser.roles)) {
    return nextStageForAdmin(adminUser) === 'completed' ? '/admin' : '/admin/onboarding';
  }

  const companyOwnerUser = getCompanyOwnerAuthUser();
  if (isCompanyOwnerUser(companyOwnerUser)) {
    return getCompanyOwnerNextRoute(getCompanyOwnerCompany());
  }

  const driverUser = getDriverAuthUser();
  if (driverUser) {
    const nextStage = nextStageForDriver(driverUser);
    return nextStage === 'completed'
      ? '/driver/account-ready'
      : '/register?resume=login&role=driver';
  }

  const customerUser = getCustomerAuthUser();
  if (customerUser) {
    const nextStage = nextStageForCustomer(customerUser);
    return nextStage === 'completed'
      ? '/schedule-booking'
      : '/register?resume=login&role=customer';
  }

  return null;
}

export function getCurrentSessionRoleLabel(): string | null {
  const adminUser = getAdminAuthUser();
  if (adminUser && isAdminRole(adminUser.roles)) {
    return 'admin';
  }

  const companyOwnerUser = getCompanyOwnerAuthUser();
  if (isCompanyOwnerUser(companyOwnerUser)) {
    return 'company owner';
  }

  if (getDriverAuthUser()) {
    return 'driver';
  }

  if (getCustomerAuthUser()) {
    return 'customer';
  }

  return null;
}

export function tokenHasCompanyOwnerRole(token: string): boolean {
  const payload = decodeJwtPayload(token);
  if (!payload) return false;

  const directRoles = Array.isArray(payload.roles) ? payload.roles : [];
  const authorities = Array.isArray(payload.authorities) ? payload.authorities : [];
  const scopes = typeof payload.scope === 'string' ? payload.scope.split(' ') : [];

  return isCompanyOwnerRole(
    [...directRoles, ...authorities, ...scopes].map((value) => String(value)),
  );
}

export function formatCurrency(value: string | number | null | undefined): string {
  const numericValue =
    typeof value === 'number' ? value : value ? Number.parseFloat(String(value)) : 0;

  if (!Number.isFinite(numericValue)) {
    return 'R0.00';
  }

  return new Intl.NumberFormat('en-ZA', {
    currency: 'ZAR',
    style: 'currency',
    minimumFractionDigits: 2,
  }).format(numericValue);
}

export function formatDateTime(value: string | null | undefined): string {
  if (!value) return 'Not available';

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'Not available';

  return new Intl.DateTimeFormat('en-ZA', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(parsed);
}

export function formatRelativeStatusTime(value: string | null | undefined): string {
  if (!value) return 'No recent update';

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'No recent update';

  const diffMs = Date.now() - parsed.getTime();
  const diffMinutes = Math.floor(diffMs / 60000);

  if (diffMinutes < 1) return 'Updated just now';
  if (diffMinutes < 60) return `Updated ${diffMinutes} min ago`;

  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `Updated ${diffHours} hr ago`;

  const diffDays = Math.floor(diffHours / 24);
  return `Updated ${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
}
